import { useState, useEffect } from 'react';
import { dmAutoReplyAPI, accountAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function DmAutoReplyPage() {
  const { user } = useAuth();

  const [config, setConfig] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 웰컴 메시지 편집
  const [welcomeMessage, setWelcomeMessage] = useState('');

  // 키워드 추가 모달
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newReplyMessage, setNewReplyMessage] = useState('');

  // 운영 설정
  const [operatingMode, setOperatingMode] = useState('ALWAYS');
  const [operatingDays, setOperatingDays] = useState([true, true, true, true, true, false, false]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // 계정 조회
      const accountRes = await accountAPI.getMy();
      if (accountRes.success && accountRes.data?.length) {
        const myAccount = accountRes.data.find(a => a.status === 'ACTIVE' && a.platformUserId) || accountRes.data[0];
        accountRes.data = myAccount;
        setAccount(myAccount);
        // 자동 답장 설정 조회
        const configRes = await dmAutoReplyAPI.getConfig(accountRes.data.id);
        if (configRes.success && configRes.data) {
          const cfg = configRes.data;
          setConfig(cfg);
          setWelcomeMessage(cfg.welcomeMessage || '');
          setOperatingMode(cfg.operatingMode || 'ALWAYS');
          if (cfg.operatingDays) {
            const days = cfg.operatingDays.split(',').map(Number);
            setOperatingDays([1,2,3,4,5,6,7].map(d => days.includes(d)));
          }
        }
      }
    } catch (err) {
      console.error('자동 답장 설정 로드 실패:', err);
      setError('설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    if (!config) return;
    try {
      const res = await dmAutoReplyAPI.toggleEnabled(config.id);
      if (res.success) {
        setConfig(res.data);
        showSuccessMsg(res.data.enabled ? '자동 답장이 활성화되었습니다.' : '자동 답장이 비활성화되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveWelcome = async () => {
    if (!config) return;
    try {
      setSaving(true);
      const res = await dmAutoReplyAPI.updateConfig(config.id, {
        accountId: account.id,
        welcomeMessage,
      });
      if (res.success) {
        setConfig(res.data);
        showSuccessMsg('웰컴 메시지가 저장되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOperating = async () => {
    if (!config) return;
    try {
      setSaving(true);
      const daysStr = operatingDays
        .map((selected, i) => selected ? i + 1 : null)
        .filter(d => d !== null)
        .join(',');

      const res = await dmAutoReplyAPI.updateConfig(config.id, {
        accountId: account.id,
        operatingMode,
        operatingDays: daysStr,
      });
      if (res.success) {
        setConfig(res.data);
        showSuccessMsg('운영 시간 설정이 저장되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!config || !newKeyword.trim() || !newReplyMessage.trim()) return;
    try {
      setSaving(true);
      const res = await dmAutoReplyAPI.addKeywordReply({
        configId: config.id,
        keywords: newKeyword.trim(),
        replyMessage: newReplyMessage.trim(),
      });
      if (res.success) {
        await loadData();
        setNewKeyword('');
        setNewReplyMessage('');
        setShowAddModal(false);
        showSuccessMsg('키워드 답장이 추가되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKeyword = async (replyId) => {
    if (!confirm('이 키워드 답장을 삭제하시겠습니까?')) return;
    try {
      const res = await dmAutoReplyAPI.deleteKeywordReply(replyId);
      if (res.success) {
        await loadData();
        showSuccessMsg('키워드 답장이 삭제되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const showSuccessMsg = (msg) => {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 3000);
  };

  const toggleDay = (index) => {
    const newDays = [...operatingDays];
    newDays[index] = !newDays[index];
    setOperatingDays(newDays);
  };

  const dayNames = ['월', '화', '수', '목', '금', '토', '일'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-400 text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <span className="material-symbols-outlined text-6xl text-slate-600">person_off</span>
        <p className="text-slate-400 text-lg">연결된 인스타그램 계정이 없습니다</p>
        <p className="text-slate-500 text-sm">자동화 설정에서 계정을 먼저 등록해주세요</p>
      </div>
    );
  }

  // 미리보기용 첫 2개 키워드 답장
  const previewReplies = config?.keywordReplies?.slice(0, 2) || [];

  return (
    <div>
      {/* 헤더 */}
      <div className="flex flex-col gap-1 sm:gap-2 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight">Instagram DM 자동 답장</h2>
        <p className="text-slate-400 text-sm sm:text-base">키워드와 운영 시간에 따른 맞춤형 자동 응답을 구성하세요.</p>
      </div>

      {/* 알림 메시지 */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl">
          {success}
        </div>
      )}

      {/* 글로벌 토글 카드 */}
      <div className="p-4 sm:p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div className="flex items-center gap-4">
          <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="material-symbols-outlined text-primary text-2xl">verified_user</span>
          </div>
          <div>
            <p className="text-lg font-bold">자동 답장 서비스 활성화</p>
            <p className="text-slate-400 text-sm">
              {config?.enabled
                ? '현재 모든 설정된 자동 응답 기능이 동작 중입니다.'
                : '자동 답장이 비활성화 상태입니다.'}
            </p>
          </div>
        </div>
        <label className="relative flex h-8 w-14 cursor-pointer items-center rounded-full p-1 transition-colors"
          style={{ background: config?.enabled ? '#1313ec' : '#334155' }}
        >
          <input type="checkbox" className="sr-only peer" checked={config?.enabled || false} onChange={handleToggle} />
          <div className="h-6 w-6 rounded-full bg-white shadow-md transition-all"
            style={{ transform: config?.enabled ? 'translateX(24px)' : 'translateX(0)' }}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* 좌측: 설정 영역 */}
        <div className="xl:col-span-2 flex flex-col gap-8">

          {/* 키워드 기반 답장 관리 */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">key</span>
                키워드 기반 답장 관리
              </h3>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-all"
              >
                <span className="material-symbols-outlined text-sm">add</span> 키워드 추가
              </button>
            </div>

            {/* 모바일: 카드 리스트 */}
            <div className="sm:hidden space-y-2">
              {config?.keywordReplies?.length > 0 ? (
                config.keywordReplies.map(reply => (
                  <div key={reply.id} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-primary">{reply.keywords}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{reply.replyMessage}</p>
                      </div>
                      <button onClick={() => handleDeleteKeyword(reply.id)} className="text-slate-400 hover:text-red-500 shrink-0">
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">
                  등록된 키워드가 없습니다
                </div>
              )}
            </div>

            {/* 데스크탑: 테이블 */}
            <div className="hidden sm:block overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3 font-semibold">키워드</th>
                    <th className="px-4 py-3 font-semibold">응답 메시지</th>
                    <th className="px-4 py-3 font-semibold text-right">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {config?.keywordReplies?.length > 0 ? (
                    config.keywordReplies.map(reply => (
                      <tr key={reply.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                        <td className="px-4 py-4 font-medium text-primary">{reply.keywords}</td>
                        <td className="px-4 py-4 text-slate-600 dark:text-slate-300">{reply.replyMessage}</td>
                        <td className="px-4 py-4 text-right">
                          <button onClick={() => handleDeleteKeyword(reply.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-4 py-8 text-center text-slate-500">
                        등록된 키워드가 없습니다. "키워드 추가" 버튼으로 추가하세요.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* 웰컴 메시지 */}
          <section className="flex flex-col gap-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">waving_hand</span>
              기본 웰컴 메시지
            </h3>
            <div className="p-6 rounded-xl border border-slate-800 bg-slate-900/40 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-slate-400">첫 DM 발송 시 자동 전송되는 메시지</label>
                <textarea
                  className="w-full rounded-lg bg-slate-800 border-slate-700 text-white p-4 focus:ring-primary focus:border-primary resize-none"
                  rows="4"
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleSaveWelcome}
                  disabled={saving}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg font-bold text-sm hover:bg-slate-600 transition-colors disabled:opacity-50"
                >
                  {saving ? '저장 중...' : '변경사항 저장'}
                </button>
              </div>
            </div>
          </section>

          {/* 운영 시간 설정 */}
          <section className="flex flex-col gap-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">schedule</span>
              운영 시간 설정
            </h3>
            <div className="p-6 rounded-xl border border-slate-800 bg-slate-900/40">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-4">
                  <p className="text-sm font-semibold text-slate-400">자동 답장 적용 범위</p>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="op_mode"
                        checked={operatingMode === 'ALWAYS'}
                        onChange={() => setOperatingMode('ALWAYS')}
                        className="text-primary focus:ring-primary bg-slate-800 border-slate-600"
                      />
                      <span className="text-sm">24시간 항상 작동</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="op_mode"
                        checked={operatingMode === 'BUSINESS_HOURS_ONLY'}
                        onChange={() => setOperatingMode('BUSINESS_HOURS_ONLY')}
                        className="text-primary focus:ring-primary bg-slate-800 border-slate-600"
                      />
                      <span className="text-sm">영업 외 시간(부재 시)만 작동</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <p className="text-sm font-semibold text-slate-400">운영 요일 선택</p>
                  <div className="flex gap-2">
                    {dayNames.map((name, index) => (
                      <button
                        key={index}
                        onClick={() => toggleDay(index)}
                        className={`w-8 h-8 rounded text-xs font-bold transition-colors ${
                          operatingDays[index]
                            ? 'bg-primary text-white'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSaveOperating}
                  disabled={saving}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg font-bold text-sm hover:bg-slate-600 transition-colors disabled:opacity-50"
                >
                  {saving ? '저장 중...' : '운영 설정 저장'}
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* 우측: 미리보기 */}
        <div className="flex flex-col gap-4">
          <h3 className="text-lg sm:text-xl font-bold flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">visibility</span>
            미리보기
          </h3>
          <div className="xl:sticky xl:top-24 mx-auto w-full max-w-[280px] sm:max-w-[320px]">
            <div className="border-[6px] border-slate-800 rounded-[2.5rem] bg-black overflow-hidden shadow-2xl"
              style={{ aspectRatio: '9/18' }}
            >
              {/* 폰 헤더 */}
              <div className="p-4 border-b border-slate-900 flex items-center gap-3">
                <span className="material-symbols-outlined text-slate-400 text-sm">arrow_back_ios</span>
                <div className="size-8 rounded-full bg-slate-800" />
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold">{account?.username || 'InstaBot'}</span>
                  <span className="text-[8px] text-slate-500">Instagram</span>
                </div>
              </div>
              {/* 채팅 영역 */}
              <div className="p-4 flex flex-col gap-4 overflow-y-auto" style={{ height: 'calc(100% - 120px)' }}>
                {previewReplies.length > 0 ? (
                  previewReplies.map((reply, i) => (
                    <div key={i}>
                      {/* 수신 메시지 */}
                      <div className="flex items-end gap-2 max-w-[80%] mb-2">
                        <div className="size-6 rounded-full bg-slate-700 flex-shrink-0" />
                        <div className="bg-slate-800 text-white p-3 rounded-2xl rounded-bl-none text-[11px] leading-relaxed">
                          {reply.keywords.split(' ').find(k => k.startsWith('#'))
                            ? `${reply.keywords} 관련해서 궁금해요`
                            : `${reply.keywords} 관련 문의드립니다`}
                        </div>
                      </div>
                      {/* 응답 메시지 */}
                      <div className="flex flex-col items-end gap-1">
                        <div className="bg-primary text-white p-3 rounded-2xl rounded-br-none text-[11px] leading-relaxed max-w-[85%] shadow-sm">
                          {reply.replyMessage}
                        </div>
                        <span className="text-[8px] text-slate-500 mr-1">방금 전 · 자동 답장</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <span className="text-slate-600 text-[11px]">키워드를 추가하면<br/>미리보기가 표시됩니다</span>
                  </div>
                )}
              </div>
              {/* 폰 푸터 */}
              <div className="absolute bottom-0 left-0 w-full p-4 bg-black border-t border-slate-900">
                <div className="bg-slate-900 rounded-full px-4 py-2 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400">메시지 보내기...</span>
                  <span className="material-symbols-outlined text-slate-400 text-lg">image</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 키워드 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">키워드 답장 추가</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">트리거 키워드</label>
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="#가격 #얼마"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-primary focus:border-primary"
                />
                <p className="text-xs text-slate-500 mt-1">공백으로 구분하여 여러 키워드를 입력할 수 있습니다</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">자동 응답 메시지</label>
                <textarea
                  value={newReplyMessage}
                  onChange={(e) => setNewReplyMessage(e.target.value)}
                  placeholder="키워드가 감지되면 전송할 메시지를 입력하세요"
                  rows="4"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-primary focus:border-primary resize-none"
                />
              </div>

              <div className="flex gap-3 justify-end mt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-5 py-2.5 bg-slate-700 text-white rounded-lg font-bold text-sm hover:bg-slate-600 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleAddKeyword}
                  disabled={saving || !newKeyword.trim() || !newReplyMessage.trim()}
                  className="px-5 py-2.5 bg-primary text-white rounded-lg font-bold text-sm hover:bg-primary/90 transition-all disabled:opacity-50"
                >
                  {saving ? '추가 중...' : '추가'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DmAutoReplyPage;
